import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { fetchStripBoard } from '../../strip-board/strip-board.slice';
import { ShootingSchedule } from '../../schedule/components/ShootingSchedule';
import { Project, ShootingDay, Shot } from '../../../app/entities';
import { Header } from '../../../components/table/styled-shared-table';
import { EmptyTablePlaceholder } from '../../../components/table/empty-table-placeholder';
import { context } from '../../../app/context';

import type { Step } from '../../../app/entities/step';
import type { DayBreakStripData } from '../../../app/entities/strip';

interface ShootingDayScheduleProps {
  projectId: string;
  shootingDayId: string;
  step: Step;
  dueDate?: string;
}

export const ShootingDaySchedule: React.FC<ShootingDayScheduleProps> = observer(
  ({ projectId, shootingDayId, step, dueDate }) => {
    const [state, setState] = useState('loading');
    const [rows, setRows] = useState<any[]>([]);
    const project = Project.getOne(projectId)!;
    const view = step.resource;
    const stripBoard = project.stripboard;
    const currentMember = context.member;

    useEffect(() => {
      fetchStripBoard(projectId).then(() => {
        setState('ready');
      });
    }, [projectId]);

    useEffect(() => {
      if (state !== 'ready' || !stripBoard?.strips) return;

      let startIndex = 0;
      let endIndex = 0;

      /**
       * Find all strips corresponding to this shooting day;
       * We use the for loop to break once we found the corresponding shooting day;
       */
      for (let i = 0; i < stripBoard.strips.length; i++) {
        const strip = stripBoard.strips[i];

        if (
          strip.type === 'dayBreak' &&
          (strip.data as DayBreakStripData)!.stepId === shootingDayId
        ) {
          endIndex = i + 1;
          break;
        }

        if (strip.type === 'dayBreak') {
          startIndex = i + 1;
        }
      }

      const strips = stripBoard.strips
        .slice(startIndex, endIndex)
        .filter((strip) => !strip.data.isHidden);

      setRows(strips);
    }, [stripBoard?.strips, state, shootingDayId, step.info?.generalCallTime]);

    if (state !== 'ready' || !stripBoard || !view) {
      return null;
    }

    const stripIds = stripBoard.strips.map((strip: any) => strip._id);
    const totalShootingDays = stripBoard.totalShootingDays;
    const currShootingDay = stripBoard.shootingDays.find(
      (shootingDay) => shootingDay._id === shootingDayId,
    );

    const sortableStripIds = [...stripBoard.sortableStripIds];

    const userStepPreferences = currentMember?.preferences.stepsUI[step._id];

    const getStripPosition = (stripIndex: number) => {
      if (stripIndex < 0 || stripIndex >= stripIds.length) return undefined;

      const destination = stripBoard.strips[stripIndex];

      if (destination === undefined) {
        return;
      }

      return destination.position;
    };

    const onUpdateStrip = async (
      stripId: string,
      update: {
        data?: {
          text?: string;
          estimatedTime?: number;
          isHidden?: boolean;
        };
      },
    ) => {
      await stripBoard.editStrip(stripId, update);
    };

    const onResortStrips = async (selectedStripIds: string[], newPosition: number) => {
      await stripBoard.edit(selectedStripIds, newPosition);
    };

    const handleAddShotToShootingDay = (shootingDay: ShootingDay) => {
      const endOfDayStrip = shootingDay.strips[shootingDay.strips.length - 1];

      Shot.createShot({
        projectId: project._id,
        stripPosition: stripBoard.getPreviousShootingDayPosition(endOfDayStrip._id),
        shootingDayId: shootingDay._id,
      });
    };

    return (
      <div>
        <Header>
          <h1>Shooting Schedule</h1>
        </Header>

        {stripBoard ? (
          rows.length > 0 ? (
            <ShootingSchedule
              placeholder={
                rows.length === 1 && (
                  <EmptyTablePlaceholder description="There are no shots scheduled today." />
                )
              }
              options={userStepPreferences}
              stripboard={stripBoard}
              strips={stripBoard.strips}
              properties={view.visibleProperties}
              shootingDays={[currShootingDay]}
              project={project}
              stripIds={stripIds}
              sortableStripIds={sortableStripIds}
              totalShootingDays={totalShootingDays}
              unscheduledStrips={[]}
              onResortStrips={onResortStrips}
              onUpdateStrip={onUpdateStrip}
              onAddShotToShootingDay={handleAddShotToShootingDay}
              getStripPosition={getStripPosition}
              hideUnscheduledTable
              scroll={false}
            />
          ) : (
            // TODO: should have a loader instead as empty placeholder is inside ShootingSchedule
            <EmptyTablePlaceholder description="There are no shots scheduled today." />
          )
        ) : null}
      </div>
    );
  },
);
